import React from "react";
import { attachRouteMapBasemap } from "../../utils/routeMapBasemap.js";
import { ADDRESS_COLOR, DEPOT_COLOR, OFF_COLOR } from "./geotabFormat.js";
import "./GeotabMap.css";

const escapeHtml = (text) => String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// « Titre — détail » devient un survol au style des hovercards du SaaS.
function hoverCard(layer, text, options = {}) {
  const [title, ...rest] = String(text ?? "").split(" — ");
  const html = `<span class="geotab-hovercard-title">${escapeHtml(title)}</span>${rest.length ? escapeHtml(rest.join(" — ")) : ""}`;
  return layer.bindTooltip(html, { direction: "top", className: "geotab-hovercard", ...options });
}

const SITE_BORDER = { loading: "#4f6d96", delivery: "#8a6a2a", depot: DEPOT_COLOR };

function sitePinHtml(site) {
  const color = site.missing ? "#c5524a" : SITE_BORDER[site.kind] || DEPOT_COLOR;
  return `<div style="transform:translate(-50%,-50%);width:20px;height:20px;border-radius:3px;background:#fff;border:2px ${site.missing ? "dashed" : "solid"} ${color};color:${color};font:800 11px/16px 'Plus Jakarta Sans',sans-serif;text-align:center">${site.label}</div>`;
}

function truckPinHtml(truck) {
  return `<div style="transform:translate(-50%,-50%);display:inline-block;white-space:nowrap;padding:1px 5px;border-radius:3px;background:${truck.color};color:#fff;font:800 11px/16px 'Plus Jakarta Sans',sans-serif;border:1px solid rgba(0,0,0,.35);box-shadow:0 1px 3px rgba(0,0,0,.3);${truck.selected ? "outline:2px solid #facc15;" : ""}">${truck.label}</div>`;
}

// Carte Geotab sur le même fond que le Dispatch. layers : plannedLine, realLine, sites, realStops, offsetLinks, trucks, depot.
export default function GeotabMap({ layers, fitKey, className = "", onTruckClick, fitOnResize = false }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const groupRef = React.useRef(null);
  const lastFitRef = React.useRef(null);
  const boundsRef = React.useRef([]);
  const clickRef = React.useRef(onTruckClick);
  clickRef.current = onTruckClick;

  React.useEffect(() => {
    const L = window.L;
    if (!L || !containerRef.current) return undefined;
    const map = L.map(containerRef.current, { zoomControl: true });
    const disposeBasemap = attachRouteMapBasemap(map, { L });
    map.setView([45.7, -73.0], 8);
    groupRef.current = L.featureGroup().addTo(map);
    mapRef.current = map;
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      if (fitOnResize && boundsRef.current.length) map.fitBounds(L.latLngBounds(boundsRef.current), { padding: [28, 28], maxZoom: 13, animate: false });
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      disposeBasemap?.();
      map.remove();
      mapRef.current = null;
    };
  }, [fitOnResize]);

  React.useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    const group = groupRef.current;
    if (!L || !map || !group) return;
    group.clearLayers();
    const bounds = [];
    const icon = (html) => L.divIcon({ className: "", html, iconSize: [0, 0] });
    if (layers.plannedLine?.length > 1) {
      L.polyline(layers.plannedLine, { color: "#0f5c57", weight: 2.5, dashArray: "6 6", opacity: 0.85 }).addTo(group);
      bounds.push(...layers.plannedLine);
    }
    if (layers.realLine?.length > 1) {
      L.polyline(layers.realLine, { color: "#141414", weight: 3, opacity: 0.85 }).addTo(group);
      bounds.push(...layers.realLine);
    }
    (layers.offsetLinks || []).forEach(([from, to, title]) => {
      hoverCard(L.polyline([from, to], { color: "#c5524a", weight: 1.5, dashArray: "2 4" }), title).addTo(group);
    });
    if (layers.depot) {
      hoverCard(L.marker(layers.depot, { icon: icon(sitePinHtml({ label: "D", kind: "depot" })) }), "Dépôt prévu").addTo(group);
      bounds.push(layers.depot);
    }
    (layers.sites || []).forEach((site) => {
      hoverCard(L.marker([site.lat, site.lon], { icon: icon(sitePinHtml(site)) }), site.title).addTo(group);
      bounds.push([site.lat, site.lon]);
    });
    (layers.realStops || []).forEach((stop) => {
      const color = stop.off ? OFF_COLOR : "#141414";
      hoverCard(L.marker([stop.lat, stop.lon], {
        icon: icon(`<div style="transform:translate(-50%,-50%);width:9px;height:9px;background:${color};border:1.5px solid #fff;box-shadow:0 0 0 1px ${color}"></div>`),
      }), stop.title).addTo(group);
    });
    (layers.trucks || []).forEach((truck) => {
      hoverCard(L.marker([truck.lat, truck.lon], { icon: icon(truckPinHtml(truck)), zIndexOffset: truck.selected ? 1000 : 0 })
        .on("click", () => clickRef.current?.(truck.id)), truck.title, { offset: [0, -10] })
        .addTo(group);
      if (!bounds.length) bounds.push([truck.lat, truck.lon]);
    });
    if (layers.fitTrucks) (layers.trucks || []).forEach((t) => bounds.push([t.lat, t.lon]));
    boundsRef.current = bounds;
    // Recadrer seulement quand la sélection change, pas à chaque rafraîchissement des positions.
    if (bounds.length && lastFitRef.current !== fitKey) {
      // Sans animation : un recadrage animé interrompu par un rechargement finirait sur une carte retirée.
      map.fitBounds(L.latLngBounds(bounds), { padding: [28, 28], maxZoom: 13, animate: false });
      lastFitRef.current = fitKey;
    }
  }, [layers, fitKey]);

  return <div ref={containerRef} className={`relative z-0 overflow-hidden rounded-[4px] border border-slate-200 bg-slate-100 ${className}`} />;
}
